import { useEffect, useMemo, useState, useRef } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useExamApi } from "../context"
import { presets } from "../themePresets"
import { motion, AnimatePresence } from "framer-motion"
import { Award, RefreshCcw, Shield, Crown, Trophy, Eye, EyeOff, CheckCircle, XCircle, Timer, ChevronLeft, ChevronRight, CheckCircle2, Palette, FileText, Printer } from "lucide-react"
import SubmissionSolution from "../../components/SubmissionSolution"

const fade = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: 16 }, transition: .25 }
const fmtThai = s => { const v = Number(s || 0); const m = Math.floor(v / 60); const ss = Math.max(0, v % 60); return `${m} นาที ${ss} วินาที` }
const awardTint = a => a === "gold" ? "text-amber-600" : a === "silver" ? "text-zinc-600" : a === "bronze" ? "text-orange-700" : "text-black/70"
const isUUID = s => typeof s === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[12][0-9a-f]{11}$/i.test(s).valueOf() ? true : /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s)


function ScrollingName({ text, className = "" }) {
  const containerRef = useRef(null)
  const textRef = useRef(null)
  const [distance, setDistance] = useState(0)
  const [shouldScroll, setShouldScroll] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    const content = textRef.current
    if (!container || !content) return
    const measure = () => {
      const containerWidth = container.clientWidth
      const contentWidth = content.scrollWidth
      const needsScroll = contentWidth > containerWidth + 4
      setShouldScroll(needsScroll)
      setDistance(needsScroll ? Math.max(0, contentWidth - containerWidth) : 0)
    }
    measure()
    window.addEventListener("resize", measure)
    return () => window.removeEventListener("resize", measure)
  }, [text])

  const duration = distance ? Math.max(6, distance / 12) : 0

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      <motion.div
        key={text}
        ref={textRef}
        className={shouldScroll ? "whitespace-nowrap inline-flex" : "truncate"}
        animate={shouldScroll && distance > 0 ? { x: [0, -distance, 0] } : { x: 0 }}
        transition={shouldScroll && distance > 0 ? { duration, repeat: Infinity, ease: "linear" } : { duration: 0 }}
      >
        {text}
      </motion.div>
    </div>
  )
}


function normalizeOptions(raw) {
  if (!raw) return []
  if (Array.isArray(raw)) {
    if (raw.length && typeof raw[0] === "object" && raw[0] && "key" in raw[0]) {
      return raw.map(o => ({ key: String(o.key).toLowerCase(), label: (String(o.key).match(/\d+/) || [""])[0], text: o.description || o.text || "" }))
    }
    return raw.map(o => {
      if (o && typeof o === "object") {
        const [k, v] = Object.entries(o)[0] || []
        const text = v && typeof v === "object" ? (v.description || v.text || "") : (v || "")
        return { key: String(k).toLowerCase(), label: (String(k).match(/\d+/) || [""])[0], text: String(text) }
      }
      return { key: "", label: "", text: String(o) }
    })
  }
  if (typeof raw === "object") {
    return Object.entries(raw).map(([k, v]) => {
      const text = v && typeof v === "object" ? (v.description || v.text || "") : (v || "")
      return { key: String(k).toLowerCase(), label: (String(k).match(/\d+/) || [""])[0], text: String(text) }
    })
  }
  try { const parsed = JSON.parse(raw); return normalizeOptions(parsed) } catch { return [] }
}

export default function ExamResult({ scoreParam, fullParam, spendParam, onRetry }) {
  const { api, theme, setTheme, t } = useExamApi()
  const { runId: runIdFromParam, sid: sidFromParam } = useParams()
  const [sp] = useSearchParams()
  const navigate = useNavigate()
  const [meta, setMeta] = useState(null)
  const [rank, setRank] = useState([])
  const [mine, setMine] = useState(null)
  const [showSol, setShowSol] = useState(false)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState("")
  const [themeOpen, setThemeOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(sessionStorage.getItem("kuizy_theme_collapsed") === "1")
  const [sidStored, setSidStored] = useState("")
  const applyTheme = v => { setTheme(v); localStorage.setItem("kuizy_theme", v); setThemeOpen(false) }
  const toggleCollapse = () => { const v = !collapsed; setCollapsed(v); sessionStorage.setItem("kuizy_theme_collapsed", v ? "1" : "0"); if (v) setThemeOpen(false) }
  const preview = sp.get("preview") === "1" || sp.get("mode") === "test"
  const sidParam = sidFromParam || sp.get("sid") || ""
  const runIdEffective = runIdFromParam || api.getRunId?.() || ""
  const [resolvedSid, setResolvedSid] = useState(sidParam)
  const [viewSubmission, setViewSubmission] = useState(null)
  const [activeKey, setActiveKey] = useState(0)

  const [admin, setAdmin] = useState(false)

  useEffect(() => {
    if (sidParam === "z") {
      setAdmin(true)
    } else {
      setAdmin(false)
    }
  }, [sidParam])

  useEffect(() => {
    ; (async () => {
      try {
        setLoading(true)
        const m = await api.getOverview()
        let s = sidParam
        if ((!s || !isUUID(s)) && api.kind === "learner") {
          try { s = sessionStorage.getItem(`kuizy_last_submission_${runIdEffective}`) || "" } catch { s = "" }
        }
        setResolvedSid(s || "")
        const shouldFetchRanking = api.kind === "public" ? (!preview && m?.settings?.showRanking) : true
        const rk = shouldFetchRanking ? await api.getRanking() : []
        const detail = (s && isUUID(s)) ? await api.getSubmissionDetail(s) : null
        const rows = (rk || []).map((r, i) => ({ ...r, rank: r.rank || (i + 1), award: r.award || (i === 0 ? "gold" : i === 1 ? "silver" : i === 2 ? "bronze" : null) }))
        setMeta(m || {})
        setRank(rows)
        setMine(detail)
        setErr("")
      } catch { setErr("โหลดผลสอบไม่สำเร็จ") } finally { setLoading(false) }
    })()
  }, [api, sidParam, runIdEffective, preview])

  const score = useMemo(() => Number(scoreParam ?? sp.get("score") ?? mine?.score ?? 0), [scoreParam, sp, mine])
  const full = useMemo(() => Number(fullParam ?? sp.get("full") ?? mine?.full_score ?? 0), [fullParam, sp, mine])
  const spend = useMemo(() => Number(spendParam ?? sp.get("spend") ?? mine?.spend_sec ?? 0), [spendParam, sp, mine])
  const percent = full ? Math.round((score / full) * 100) : 0
  const passed = meta ? percent >= (meta.test?.criteria || 0) : false
  const hasSid = resolvedSid && isUUID(resolvedSid)
  const canSeeSolution = api.kind === "public" ? (hasSid && meta?.settings?.showSolution !== "none") : hasSid
  const showQuestionOnly = api.kind === "public" ? (meta?.settings?.showSolution === "question") : false
  const showRankingBlock = api.kind === "public" ? (!preview && meta?.settings?.showRanking) : true
  const goAgain = () => onRetry ? onRetry() : navigate(-1)

  if (loading) return <div className={`min-h-screen grid place-items-center ${t.bg} ${t.text}`}>กำลังโหลด...</div>
  if (err) return <div className={`min-h-screen grid place-items-center ${t.bg} text-danger`}>{err}</div>
  if (!meta) return null

  const top3 = rank.slice(0, 3)
  const rest = rank.slice(3)
  const topCardTone = a => a === "gold" ? "border-amber-300/30 from-amber-300/10 to-amber-600/5" : a === "silver" ? "border-zinc-300/30 from-zinc-300/10 to-zinc-600/5" : "border-orange-300/30 from-orange-300/10 to-orange-600/5"
  const ringGradAward = (pct, a) => {
    const base = t.key === "dark" ? "#374151" : "#e5e7eb"
    const col = a === "gold" ? "#f59e0b" : a === "silver" ? "#9ca3af" : "#d97706"
    return { background: `conic-gradient(${col} ${pct}%, ${base} ${pct}% 100%)` }
  }

  const openDetailSubmittion = (submissionId) => {
    if (!submissionId) return
    window.open(`/reports/result/${submissionId}`, "_blank")
  }

  const printExamReport = (sub, answers, participantName) => {
      const toArr = v => Array.isArray(v) ? v : (v != null && v !== "" ? [v] : [])
      const norm = s => (s || "").toLowerCase()
      const keyToLetter = key => { const m = (key || "").match(/\d+/); return m ? String.fromCharCode(64 + parseInt(m[0])) : key }
      const now = new Date()
      const thDate = now.toLocaleDateString("th-TH", { year: "numeric", month: "long", day: "numeric" })
      const thTime = now.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })
      const fullName = participantName || "-"
      const criteria = meta?.test?.criteria || sub?.criteria

      const questionsHtml = (answers || []).map((a, qi) => {
          const selected = toArr(a.selected)
          const correct = toArr(a.correct)
          const opts = normalizeOptions(a.options).map((optObj, oi) => {
              const rawKey = optObj.key
              const letter = optObj.label || keyToLetter(rawKey)
              const desc = optObj.text
              const isSel = selected.some(s => norm(s) === norm(rawKey))
              const isCorr = correct.some(c => norm(c) === norm(rawKey))
              let bg = ""
              let mark = ""
              if (isSel && isCorr) { bg = "background:#d1fae5;"; mark = " ✓" }
              else if (isCorr && !isSel) { bg = "background:#dbeafe;"; mark = " ✓" }
              else if (isSel && !isCorr) { bg = "background:#fee2e2;"; mark = " ✗" }
              return `<div style="display:flex;align-items:flex-start;gap:8px;padding:5px 8px;border-radius:6px;margin-bottom:4px;${bg}">
                  <span style="min-width:24px;height:24px;border-radius:50%;border:1.5px solid #374151;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;flex-shrink:0;">${letter}</span>
                  <span style="font-size:13px;line-height:1.5;">${desc}${mark}</span>
              </div>`
          }).join("")
          const selLetters = selected.map(s => keyToLetter(s)).join(", ") || "-"
          const corrLetters = correct.map(c => keyToLetter(c)).join(", ")
          const isCorrect = a.choice?.is_correct ?? (a.awarded > 0)
          return `<div style="margin-bottom:20px;page-break-inside:avoid;">
              <div style="display:flex;align-items:flex-start;gap:10px;margin-bottom:8px;">
                  <span style="min-width:28px;height:28px;border-radius:50%;background:${isCorrect ? "#22c55e" : "#f87171"};color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0;margin-top:1px;">${a.no}</span>
                  <div style="flex:1;">
                      <p style="margin:0 0 8px;font-size:13.5px;font-weight:600;line-height:1.6;">${a.question}</p>
                      <div>${opts}</div>
                      <div style="margin-top:6px;padding:4px 10px;border-radius:6px;border:1px solid #e5e7eb;background:#f9fafb;display:flex;gap:24px;font-size:11px;color:#6b7280;">
                          <span>คำตอบที่เลือก: <strong style="color:#374151">${selLetters}</strong></span>
                          <span>คำตอบที่ถูก: <strong style="color:#15803d">${corrLetters}</strong></span>
                          <span>คะแนน: <strong style="color:#374151">${a.awarded ?? 0}/${a.max_points ?? 1}</strong></span>
                      </div>
                  </div>
              </div>
          </div>`
      }).join("")

      const testName = meta?.test?.name || sub?.test_name || "ผลการสอบ"
      const courseName = meta?.course?.name || sub?.course_name || ""
      const scorePct = sub?.score_pct ?? (sub?.full_score ? Math.round((sub.score / sub.full_score) * 100) : 0)
      const passedProp = sub?.passed ?? (scorePct >= (criteria ?? 0))

      const html = `<!DOCTYPE html><html lang="th"><head><meta charset="UTF-8">
      <title>ข้อสอบ - ${testName}</title>
      <style>
          @import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@400;500;600;700&display=swap');
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: 'Sarabun', 'TH Sarabun New', sans-serif; font-size: 14px; color: #1f2937; background: #fff; padding: 0; }
          .page { width: 210mm; min-height: 297mm; margin: 0 auto; padding: 15mm 20mm; }
          .header { text-align: center; border-bottom: 3px double #1e40af; padding-bottom: 12px; margin-bottom: 16px; }
          .header h1 { font-size: 20px; font-weight: 700; color: #1e3a8a; letter-spacing: 0.5px; }
          .header h2 { font-size: 16px; font-weight: 600; color: #1e40af; margin-top: 4px; }
          .header p { font-size: 13px; color: #4b5563; margin-top: 4px; }
          .info-table { width: 100%; border-collapse: collapse; margin-bottom: 14px; font-size: 13px; }
          .info-table td { padding: 5px 8px; border: 1px solid #d1d5db; }
          .info-table .label { font-weight: 600; width: 28%; background: #eff6ff; }
          .score-box { display: flex; justify-content: flex-end; margin-bottom: 14px; }
          .score-inner { border: 2px solid #1e40af; border-radius: 10px; padding: 10px 24px; text-align: center; min-width: 180px; }
          .score-inner .score-num { font-size: 28px; font-weight: 700; color: #1e3a8a; }
          .score-inner .score-label { font-size: 12px; color: #6b7280; margin-top: 2px; }
          .score-inner .pass-badge { display:inline-block; margin-top:6px; padding: 2px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; background: ${passedProp ? "#dcfce7" : "#fee2e2"}; color: ${passedProp ? "#15803d" : "#b91c1c"}; }
          .instructions { background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 10px 14px; margin-bottom: 16px; font-size: 12.5px; color: #78350f; }
          .instructions strong { display: block; margin-bottom: 4px; font-size: 13px; color: #92400e; }
          .section-title { font-size: 14px; font-weight: 700; color: #1e3a8a; border-left: 4px solid #1e40af; padding-left: 10px; margin-bottom: 14px; }
          .footer { border-top: 2px solid #1e40af; padding-top: 10px; margin-top: 20px; display: flex; justify-content: space-between; font-size: 11px; color: #6b7280; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } .page { padding: 10mm 15mm; } }
      </style></head><body>
      <div class="page">
          <div class="header">
              <h1>แบบทดสอบ / ผลการสอบ</h1>
              <h2>${testName}</h2>
              ${courseName ? `<p>วิชา: ${courseName}</p>` : ""}
          </div>

          <div style="display:flex;gap:16px;align-items:flex-start;margin-bottom:14px;">
              <table class="info-table" style="flex:1;margin-bottom:0;">
                  <tr><td class="label">ชื่อ - นามสกุล</td><td>${fullName}</td></tr>
                  ${courseName ? `<tr><td class="label">วิชา / หลักสูตร</td><td>${courseName}</td></tr>` : ""}
                  <tr><td class="label">ชุดข้อสอบ</td><td>${testName}</td></tr>
                  <tr><td class="label">วันที่สอบ</td><td>${thDate}</td></tr>
                  <tr><td class="label">เวลาสอบ</td><td>${thTime} น.</td></tr>
                  ${sub?.spend_sec != null ? `<tr><td class="label">เวลาที่ใช้</td><td>${fmtThai(sub.spend_sec)}</td></tr>` : ""}
                  ${criteria != null ? `<tr><td class="label">เกณฑ์ผ่าน</td><td>${criteria}%</td></tr>` : ""}
              </table>
              <div class="score-box" style="margin:0;">
                  <div class="score-inner">
                      <div class="score-num">${sub?.score ?? "-"}/${sub?.full_score ?? "-"}</div>
                      <div class="score-label">คะแนนที่ได้ / คะแนนเต็ม</div>
                      ${scorePct != null ? `<div style="font-size:16px;font-weight:700;color:#374151;margin-top:4px;">${Number(scorePct).toFixed(1)}%</div>` : ""}
                      <div class="pass-badge">${passedProp ? "ผ่าน" : "ไม่ผ่าน"}</div>
                  </div>
              </div>
          </div>

          <div class="instructions">
              <strong>คำชี้แจง / Instructions</strong>
              แบบทดสอบฉบับนี้เป็นข้อสอบปรนัยแบบเลือกตอบ ให้เลือกคำตอบที่ถูกต้องที่สุดเพียงข้อเดียว (เว้นแต่จะระบุไว้เป็นอย่างอื่น)
              ตัวเลือกที่เน้นด้วยสีเขียว <span style="background:#d1fae5;padding:1px 6px;border-radius:4px;">✓</span> คือคำตอบที่เลือกและถูกต้อง
              สีน้ำเงิน <span style="background:#dbeafe;padding:1px 6px;border-radius:4px;">✓</span> คือคำตอบที่ถูกต้อง (แต่ไม่ได้เลือก)
              สีแดง <span style="background:#fee2e2;padding:1px 6px;border-radius:4px;">✗</span> คือคำตอบที่เลือกแต่ผิด
          </div>

          <div class="section-title">คำถามและคำตอบ (${answers.length} ข้อ)</div>
          <div class="questions">${questionsHtml}</div>

          <div class="footer">
              <span>พิมพ์เมื่อ: ${thDate} เวลา ${thTime} น.</span>
              <span>ผู้สอบ: ${fullName}</span>
          </div>
      </div>
      <script>window.onload = () => { window.print(); }<\/script>
      </body></html>`

      const w = window.open("", "_blank", "width=900,height=700")
      if (w) { w.document.write(html); w.document.close() }
  }

  return (
    <div className={`min-h-screen p-4 sm:p-6 ${t.bg}`}>
      <div className="fixed top-3 right-3 z-50">
        {collapsed ? (
          <button onClick={toggleCollapse} className={`h-10 w-10 rounded-full shadow ${t.neutralBtn} ${t.border} border grid place-items-center`}>
            <ChevronLeft className="size-5" />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={toggleCollapse} className={`h-10 w-10 rounded-full shadow ${t.neutralBtn} ${t.border} border grid place-items-center`}>
              <ChevronRight className="size-5" />
            </button>
            <div className="relative">
              <button onClick={() => setThemeOpen(s => !s)} className={`h-10 w-10 rounded-full shadow ${t.neutralBtn} ${t.border} border grid place-items-center`}>
                <Palette className="size-5" />
              </button>
              {themeOpen && (
                <div className={`absolute right-0 mt-2 w-44 rounded-xl p-2 backdrop-blur ${t.card} ${t.text}`}>
                  <div className={`text-xs mb-2 px-1 opacity-80 ${t.subtext}`}>ธีม</div>
                  {Object.keys(presets).map(k => (
                    <button key={k} onClick={() => { setTheme(k); localStorage.setItem("kuizy_theme", k); setThemeOpen(false) }} className={`w-full h-9 px-3 rounded-lg flex items-center justify-between ${t.soft}`}>
                      <span className="capitalize">{k}</span>{theme === k ? <CheckCircle2 className="size-4 text-primary" /> : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="max-w-6xl mx-auto">
        {preview && api.kind === "public" ? (
          <div className="mb-4 rounded-xl border border-amber-400/40 bg-amber-500/10 text-amber-700 px-3 py-2 text-xs sm:text-sm inline-flex items-center gap-2">
            <Shield className="size-4" /> โหมดทดสอบ: คะแนนนี้ไม่ถูกรวมในกระดานจริง
          </div>
        ) : null}

        <motion.div {...fade} className={`rounded-2xl p-5 sm:p-6 ${t.card}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-xl bg-primary/20 grid place-items-center"><Award className="size-5 text-primary" /></div>
              <div>
                <div className={`text-lg sm:text-xl ${t.text}`}>{meta.test?.name || "ผลคะแนน"}</div>
                <div className={`text-xs ${t.subtext}`}>{meta.test?.content_name} {meta.test?.content_code ? `(${meta.test?.content_code})` : ""}</div>
              </div>
            </div>
            <img src="/images/app-logo.png" alt="Kuizy" className="h-9 opacity-90 hidden sm:block" />
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className={`rounded-xl ${t.soft} p-4 flex items-center gap-3`}>
              <div className="size-9 rounded-lg bg-green-500/10 grid place-items-center text-green-700">
                <Crown className="size-5" />
              </div>
              <div>
                <div className={`text-xs ${t.subtext}`}>คะแนน</div>
                <div className={`text-2xl ${t.text}`}>{score}/{full}</div>
              </div>
            </div>
            <div className={`rounded-xl ${t.soft} p-4 flex items-center gap-3`}>
              <div className="size-9 rounded-lg bg-blue-500/10 grid place-items-center text-blue-700">
                <Trophy className="size-5" />
              </div>
              <div>
                <div className={`text-xs ${t.subtext}`}>เปอร์เซ็นต์</div>
                <div className={`text-2xl ${t.text}`}>{full ? Math.round((score / full) * 100) : 0}%</div>
              </div>
            </div>
            <div className={`rounded-xl ${t.soft} p-4 flex items-center gap-3`}>
              <div className="size-9 rounded-lg bg-purple-500/10 grid place-items-center text-purple-700">
                <Timer className="size-5" />
              </div>
              <div>
                <div className={`text-xs ${t.subtext}`}>เวลา</div>
                <div className={`text-2xl ${t.text}`}>{fmtThai(spend)}</div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className={`px-3 py-1.5 rounded-lg text-sm ${percent >= (meta.test?.criteria ?? 0) ? "bg-green-500/15 text-green-700" : "bg-yellow-500/15 text-yellow-700"}`}>
              {percent >= (meta.test?.criteria ?? 0) ? "ผ่านเกณฑ์" : "ไม่ผ่านเกณฑ์"} • เกณฑ์ {meta.test?.criteria ?? 0}%
            </div>
            <button onClick={onRetry ? onRetry : () => { }} className={`h-9 px-4 rounded-xl inline-flex items-center gap-2 ${t.neutralBtn}`}>
              <RefreshCcw className="size-4" /> ทำใหม่
            </button>
            {canSeeSolution && (
              <button onClick={() => setShowSol(s => !s)} className="h-9 px-4 rounded-xl bg-secondary/20 hover:bg-secondary/30 text-gray-300 inline-flex items-center gap-2">
                {showSol ? <EyeOff className="size-4" /> : <Eye className="size-4" />}{showSol ? "ซ่อนเฉลย" : "ดูเฉลย"}
              </button>
            )}
          </div>
        </motion.div>

        {showRankingBlock && (
          <motion.div {...fade} className={`mt-6 rounded-2xl p-5 sm:p-6 ${t.card}`}>
            <div className="flex items-center justify-between mb-4">
              <div className={`text-lg ${t.text}`}>กระดานอันดับ</div>
              <img src="/images/app-logo.png" alt="Kuizy" className="h-8 opacity-80 sm:block hidden" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {[0, 1, 2].map(idx => {
                const r = top3[idx]
                if (!r) return <div key={idx} className={`rounded-2xl h-28 ${t.soft} ${t.border}`} />
                const pct = r.full_score ? Math.round(r.score / r.full_score * 100) : 0
                const awardBorderTone = topCardTone(r.award)
                const isChampion = idx === 0
                const nameSize = isChampion ? "text-2xl" : "text-xl"
                const ringSize = isChampion ? "size-16" : "size-14"
                const crownSize = isChampion ? "size-7" : "size-6"
                const rankSize = isChampion ? "text-2xl" : "text-xl"
                return (
                  <div key={r.submission_id || r.rank} className={`rounded-2xl border bg-gradient-to-br ${awardBorderTone} ${t.text} ${t.card} overflow-hidden`}>
                    <div className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <div className="inline-flex items-center gap-2 px-2 py-1 rounded-lg text-xs bg-black/5">
                            <Crown className={`${crownSize} ${awardTint(r.award)}`} />
                            <span className={`font-semibold ${rankSize}`}>{r.rank}</span>
                          </div>

                          <ScrollingName
                            text={r.participant}
                            className={`mt-3 font-bold ${nameSize}`}
                          />

                          <div className={`mt-1 text-sm ${t.subtext}`}>
                            {r.score}/{r.full_score} • {fmtThai(r.spend_sec)}
                          </div>
                        </div>

                        {/* กล่องขวา: % + FileText */}
                        <div className="flex flex-col items-center gap-2 shrink-0">
                          <div
                            className={`${ringSize} rounded-full relative grid place-items-center`}
                            style={ringGradAward(pct, r.award)}
                          >
                            <div className={`absolute inset-1 rounded-full ${t.ringInner}`} />
                            <div className="relative text-xs">{pct}%</div>
                          </div>
                          {admin && (
                            <div
                              className="cursor-pointer hover:text-primary mt-5"
                              onClick={async () => {
                                const detail = await api.getSubmissionDetail(r.submission_id)
                                setViewSubmission({ ...detail, participant: detail?.participant || r.participant })
                                setActiveKey(r.submission_id)
                                setShowSol(true)
                              }}
                            >
                              <FileText size={16} />
                            </div>
                          )}
                        </div>
                      </div>

                    </div>
                  </div>
                )
              })}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={t.subtext}>
                    <th className="text-left py-2 pr-3">อันดับ</th>
                    <th className="text-left py-2 pr-3">ผู้เล่น</th>
                    <th className="text-left py-2 pr-3">คะแนน</th>
                    <th className="text-left py-2 pr-3">เวลา</th>
                    <th className="text-left py-2 pr-3">เวลาส่ง</th>
                    {admin && (
                      <th className="text-left py-2 pr-3">รายละเอียด</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rest.map((r) => (
                    <tr key={r.submission_id || `${r.rank}-${r.participant}`} className={`${t.border} border-top`}>
                      <td className={`py-2 pr-3 ${t.text}`}>{r.rank}</td>
                      <td className={`py-2 pr-3 ${t.text} opacity-90`}>{r.participant}</td>
                      <td className={`py-2 pr-3 ${t.text} opacity-90`}>{r.score}/{r.full_score}</td>
                      <td className={`py-2 pr-3 ${t.text} opacity-90`}>{fmtThai(r.spend_sec)}</td>
                      <td className={`py-2 pr-3 ${t.text} opacity-70`}>{new Date(r.created_at).toLocaleString()}</td>
                      {admin && (
                        <td className={`py-2 pr-3 ${t.text} opacity-70`}>
                          <span
                            role="button"
                            className="cursor-pointer hover:text-primary"
                            onClick={async () => {
                              const detail = await api.getSubmissionDetail(r.submission_id)
                              setViewSubmission({ ...detail, participant: detail?.participant || r.participant })
                              setActiveKey(r.submission_id)
                              setShowSol(true)
                            }}
                          >
                            <FileText size={16} />
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                  {rest.length === 0 ? <tr><td colSpan={5} className={`py-4 ${t.subtext}`}>ยังไม่มีผู้เล่นเพิ่มเติม</td></tr> : null}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        <AnimatePresence>
          {showSol && hasSid && mine && (
            <motion.div {...fade} className={`mt-6 rounded-2xl p-5 sm:p-6 ${t.card}`}>
              <div className={`text-lg mb-4 ${t.text}`}>เฉลยของฉัน</div>
              <SubmissionSolution
                submission={mine}
                meta={meta}
                api={api}
                t={t}
                showQuestionOnly={showQuestionOnly}
                isAdmin={false}
              />
              {/* <div className="space-y-4">
                {(mine.answers || []).map((a, i) => {
                  const selA = Array.isArray(a.selected) ? a.selected.map(String) : (a.selected != null ? [String(a.selected)] : [])
                  const corr = Array.isArray(a.correct) ? a.correct.map(String) : (a.correct != null ? [String(a.correct)] : [])
                  const opts = normalizeOptions(a.options)
                  const correct = JSON.stringify([...selA].sort()) === JSON.stringify([...corr].sort())
                  return (
                    <div key={i} className={`rounded-xl border p-4 ${correct ? "border-green-600/40 bg-green-500/5" : "border-red-600/40 bg-red-500/5"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className={`${t.text}`}>
                          <div className={`text-xs mb-1 ${t.subtext}`}>ข้อ {a.no}</div>
                          {(showQuestionOnly || meta.settings?.showSolution === "full" || api.kind === "learner") && (
                            <div className={`${t.text} leading-relaxed`}>{a.question}</div>
                          )}
                        </div>
                        <div className="shrink-0">
                          {correct ? <CheckCircle className="size-6 text-green-600" /> : <XCircle className="size-6 text-red-600" />}
                        </div>
                      </div>

                      {(meta.settings?.showSolution === "full" || api.kind === "learner") && (
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {opts.map(o => {
                            const isSel = selA.includes(o.key)
                            const isOk = corr.includes(o.key)
                            return (
                              <div key={o.key}
                                className={`rounded-lg border px-3 py-2 text-sm
                                  ${isOk ? "border-green-500/50 bg-green-500/10 text-green-700"
                                    : isSel ? "border-red-500/50 bg-red-500/10 text-red-700"
                                      : `${t.border} ${t.soft} ${t.text}`}`}>
                                <span className="font-semibold mr-2">{o.label}.</span>
                                <span>{o.text}</span>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div> */}
            </motion.div>
          )}

          <AnimatePresence mode="wait">
            {showSol && viewSubmission && admin && (
              <motion.div key={activeKey} {...fade} className={`mt-6 rounded-2xl p-5 sm:p-6 ${t.card}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                  <div className={`text-lg ${t.text}`}>
                    เฉลยของ {viewSubmission.participant || "ผู้เข้าสอบ"}
                  </div>
                  <button
                    onClick={() => printExamReport(viewSubmission, viewSubmission.answers, viewSubmission.participant || "ผู้เข้าสอบ")}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow transition-colors"
                  >
                    <Printer size={15} /> Export PDF
                  </button>
                </div>

                <SubmissionSolution
                  submission={viewSubmission}
                  meta={meta}
                  api={api}
                  t={t}
                  showQuestionOnly={false}
                  isAdmin={true}
                />
              </motion.div>
            )}
          </AnimatePresence>

        </AnimatePresence>
      </div>
    </div>
  )
}
